import { useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, Navigate, useLocation } from 'react-router';
import { Logo } from '@/components/brand/Logo';
import { Button, Field, Input, LoadingState } from '@/components/ui';
import { env } from '@/config/env';
import { useDemoAccounts } from '@/features/auth/demo-accounts';
import type { SignInRedirectState } from '@/features/auth/guards';
import type { Role } from '@/features/auth/roles';
import { useSession } from '@/features/auth/session-context';
import { useApiHealth } from '@/features/health/use-api-health';
import { isApiError } from '@/lib/api';
import { MfaStep, RecoveryCodesStep } from './MfaStep';
import styles from './SignInPage.module.css';

const START_PAGE = '/dashboard';

/**
 * The page to open after sign-in: back to where the session expired, but only
 * for the same person; anyone else starts on their role's start page (RL-1040).
 */
function returnPath(
  redirect: SignInRedirectState | null,
  userId: string,
): string {
  if (!redirect?.from) return START_PAGE;
  if (redirect.userId && redirect.userId !== userId) return START_PAGE;
  return redirect.from;
}

function errorMessage(error: unknown, fallback: string): string {
  if (isApiError(error)) return error.message;
  return error instanceof Error ? error.message : fallback;
}

/**
 * Admin v2 sign-in: brand panel (left) + staff sign-in (right). Staff with
 * several roles then choose the role to sign in as; the tiles show only the
 * account's own roles.
 */
export function SignInPage() {
  const { t } = useTranslation();
  const { session, me, signIn, selectRole, recoveryCodes } = useSession();
  const location = useLocation();
  const redirect = location.state as SignInRedirectState | null;
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const health = useApiHealth(env.appEnv !== 'test');
  const demo = useDemoAccounts();
  const passwordRef = useRef<HTMLInputElement>(null);

  if (session.status === 'restoring')
    return <LoadingState label="Restoring your session" />;
  if (me?.activeRole && !recoveryCodes)
    return <Navigate to={returnPath(redirect, me.id)} replace />;
  const choosingRole = !!me && !me.activeRole;

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      // Navigation happens on re-render (<Navigate> below) once the session
      // state has committed, so the protected route never sees a stale session.
      await signIn(email, password);
    } catch (err) {
      setError(errorMessage(err, t('errors.genericTitle')));
    } finally {
      setBusy(false);
    }
  }

  async function onChooseRole(role: Role) {
    setError(null);
    setBusy(true);
    try {
      await selectRole(role);
    } catch (err) {
      setError(errorMessage(err, t('errors.genericTitle')));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={styles.page}>
      <section className={styles.brand} aria-label="Ommri Care">
        <Logo className={styles.logo} monochrome />
        <h1 className={styles.headline}>
          One clinic system,
          <br />
          every role.
        </h1>
        <p className={styles.lede}>
          Front desk, doctors, lab, dispensary, finance and management work from
          the same patient, queue and billing records the patient sees in the
          app.
        </p>
        <Showcase />
        <dl className={styles.promises}>
          <div>
            <dt>9</dt>
            <dd>Roles, one sign-in</dd>
          </div>
          <div>
            <dt>2</dt>
            <dd>Branches, one record</dd>
          </div>
          <div>
            <dt>3-day</dt>
            <dd>Panel claim window</dd>
          </div>
        </dl>
        <p className={styles.clinics}>
          Ommri Klinik Bachang · Ommri Klinik Sungai Udang
        </p>
      </section>

      <section className={styles.formSide}>
        {recoveryCodes ? (
          <RecoveryCodesStep />
        ) : session.status === 'mfa' ? (
          <MfaStep
            key={session.mfaToken}
            setupRequired={session.setupRequired}
          />
        ) : choosingRole ? (
          <div className={styles.form}>
            <h2 className={styles.title}>
              {t('auth.chooseRoleTitle', { name: me.fullName.split(/\s+/)[0] })}
            </h2>
            <p className={styles.subtitle}>{t('auth.chooseRoleSubtitle')}</p>
            <p className="eyebrow">{t('auth.signInAs')}</p>
            <div className={styles.tiles}>
              {me.roles.map((role) => (
                <button
                  key={role.key}
                  type="button"
                  className={styles.tile}
                  disabled={busy}
                  onClick={() => void onChooseRole(role.key)}
                >
                  <span>{role.name}</span>
                  <span className={styles.tileMeta}>
                    {role.allBranches
                      ? t('auth.allBranches')
                      : role.branches.map((b) => b.name).join(' · ')}
                  </span>
                </button>
              ))}
            </div>
            {error && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}
            <p className={styles.note}>{t('auth.rolesNote')}</p>
          </div>
        ) : (
          <form className={styles.form} onSubmit={onSubmit} noValidate>
            <h2 className={styles.title}>{t('auth.signInTitle')}</h2>
            <p className={styles.subtitle}>{t('auth.signInSubtitle')}</p>
            <Field label={t('auth.email')}>
              <Input
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="staff@ommricare.my"
                required
              />
            </Field>
            <Field label={t('auth.password')}>
              <Input
                ref={passwordRef}
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </Field>
            {/* RL-1788 */}
            <Link to="/forgot-password">{t('auth.forgotPassword')}</Link>
            {!!demo.data?.length && (
              <div className={styles.demo}>
                <p className="eyebrow">{t('auth.signInAsDemo')}</p>
                <div className={styles.roles}>
                  {demo.data.map((a) => (
                    <button
                      key={a.role}
                      type="button"
                      aria-pressed={email === a.email}
                      className={styles.roleCard}
                      onClick={() => {
                        // Prefill the QA email only; the password is typed.
                        setEmail(a.email);
                        passwordRef.current?.focus();
                      }}
                    >
                      {a.roleName}
                      <small>{a.name}</small>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {session.status === 'anonymous' &&
              session.reason === 'expired' &&
              !error && (
                <p className={styles.notice} role="status">
                  {t('auth.sessionEnded')}
                </p>
              )}
            {error && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}
            <Button
              type="submit"
              variant="primary"
              size="lg"
              block
              loading={busy}
              disabled={!email || !password}
            >
              {t('auth.signIn')}
            </Button>
            <p className={styles.note}>{t('auth.rolesNote')}</p>
            {/* Only a problem is worth saying; "API connected" was a developer
                text (RL-1793, #132). */}
            {health.isError && (
              <p className={styles.status} role="status">
                {t('auth.serverUnreachable')}
              </p>
            )}
          </form>
        )}
      </section>
    </div>
  );
}

/**
 * Decorative product cards on the brand panel (prototype .showcase). Every
 * value is illustrative, never a patient or a live record.
 */
function Showcase() {
  return (
    <div className={styles.showcase} aria-hidden="true">
      <div className={`${styles.scard} ${styles.queue}`}>
        <span className={styles.st}>Live queue</span>
        <div className={styles.row}>
          <b className={styles.big}>12</b>
          <span className={`${styles.tag} ${styles.live}`}>In room 1</span>
        </div>
        <span className={styles.sub2}>GP · panel visit</span>
        <span className={styles.qbar}>
          <i style={{ width: '62%' }} />
        </span>
        <span className={styles.sub2}>5 waiting · average 18 min</span>
      </div>
      <div className={`${styles.scard} ${styles.rx}`}>
        <span className={styles.st}>e-Prescription</span>
        <div className={styles.row}>
          <b>RX-0001</b>
          <span className={`${styles.tag} ${styles.ok}`}>Signed</span>
        </div>
        <span className={styles.sub2}>In the dispensary queue</span>
      </div>
      <div className={`${styles.scard} ${styles.claim}`}>
        <span className={styles.st}>Panel claim</span>
        <div className={styles.row}>
          <b>Claim · panel</b>
          <span className={`${styles.tag} ${styles.ready}`}>Ready</span>
        </div>
        <span className={styles.amt}>RM 85.00</span>
        <span className={styles.sla}>
          <i />
        </span>
        <span className={styles.sub2}>20 h left in the 3-day window</span>
      </div>
    </div>
  );
}
